import React, { useState, useRef, useEffect } from 'react';
import Lottie from 'lottie-react-native';
import {
  Image,
  Platform,
  StyleSheet,
  Text,
  useWindowDimensions,
  View,
} from 'react-native';

import FormGroupCheckbox from '../components/FormGroupCheckbox';
import FormGroupDatePicker from '../components/FormGroupDatePicker';
import FormGroupInput from '../components/FormGroupInput';
import FormGroupMatrix from '../components/FormGroupMatrix';
import FormGroupRadioButton from '../components/FormGroupRadioButton';
import FormGroupSelect from '../components/FormGroupSelect';
import QuestionMarker from './QuestionMarker';
import {
  IQuestionItem,
  IQuestionnaire,
  QUESTION_CONTROL_TYPE,
} from '@nocode/types/questionnaire.type';
import { FormOption } from '@nocode/types/formGroup.type';

const STEP_FORM_BUTTONS_HEIGHT = 100;

const ItemControl = ({
  item,
  width,
  dropdownContainerRef,
  styleConfig,
  dropdownMaxHeight,
}: {
  item: IQuestionItem;
  width: number;
  dropdownContainerRef: any;
  styleConfig: any;
  dropdownMaxHeight?: number;
}) => {
  if (item.options && item.control === QUESTION_CONTROL_TYPE.DROPDOWN) {
    return (
      <View ref={dropdownContainerRef}>
        <FormGroupSelect
          name={`${item.fid || item.id}`}
          options={item.options}
          placeholder="選択してください"
          styleConfig={styleConfig?.dropdown}
          required={item.required}
          maxMenuHeight={dropdownMaxHeight}
        />
      </View>
    );
  }
  if (item.options && item.control === QUESTION_CONTROL_TYPE.CHECKBOX) {
    return (
      <FormGroupCheckbox
        name={`${item.fid || item.id}`}
        options={item.options}
        styleConfig={styleConfig?.checkbox}
        required={item.required}
      />
    );
  }
  if (item.options && item.control === QUESTION_CONTROL_TYPE.RADIO) {
    return (
      <FormGroupRadioButton
        name={`${item.fid || item.id}`}
        width={width}
        options={item.options}
        styleConfig={styleConfig?.radioButton}
        required={item.required}
      />
    );
  }
  if (item.control === QUESTION_CONTROL_TYPE.INPUT) {
    return (
      <FormGroupInput
        name={`${item.fid || item.id}`}
        type={item.type || 'text'}
        styleConfig={styleConfig?.input}
        required={item.required}
      />
    );
  }
  if (item.control === QUESTION_CONTROL_TYPE.TEXTAREA) {
    return (
      <FormGroupInput
        name={`${item.fid || item.id}`}
        multiline
        type={item.type || 'text'}
        styleConfig={styleConfig?.textarea}
        required={item.required}
      />
    );
  }
  if (item.control === QUESTION_CONTROL_TYPE.DATE) {
    return (
      <FormGroupDatePicker
        name={`${item.fid || item.id}`}
        mode={item.dateMode || 'date'}
        styleConfig={styleConfig?.datePicker}
        required={item.required}
      />
    );
  }
  if (item.control === QUESTION_CONTROL_TYPE.MATRIX) {
    return (
      <FormGroupMatrix
        name={`${item.fid || item.id}`}
        range={{
          start: item.range?.start || 1,
          end: item?.range?.end || 5,
        }}
        design={item.design || 'step'}
        styleConfig={styleConfig?.matrix}
        required={item.required}
        startText={item?.startText}
        endText={item?.endText}
        options={item.options as FormOption[]}
      />
    );
  }
  return null;
};

const Question: React.FC<{
  item: IQuestionItem;
  width: number;
  questionMarker?: string;
  formType: string;
  styleConfig: any;
  index?: number;
}> = ({ item, formType, questionMarker, styleConfig, width, index }) => {
  const style = createStyles({ ...styleConfig, width });
  const [lottieError, setLottieError] = useState<string>();
  const [dropdownMaxHeight, setDropdownMaxHeight] = useState<
    number | undefined
  >(undefined);
  const dropdownContainerRef = useRef<View>(null);

  useEffect(() => {
    if (
      formType === 'step-form' &&
      item?.control === QUESTION_CONTROL_TYPE.DROPDOWN &&
      Platform.OS === 'web' &&
      typeof window !== 'undefined'
    ) {
      const calculateHeight = () => {
        const container = dropdownContainerRef.current as any;
        if (container) {
          // Try to get the actual DOM element
          let element = null;
          if (container.getBoundingClientRect) {
            element = container;
          } else if (container._nativeTag) {
            element = document.querySelector(
              `[data-tag="${container._nativeTag}"]`
            );
          }

          if (element && element.getBoundingClientRect) {
            const rect = element.getBoundingClientRect();
            const windowHeight = window.innerHeight;
            const availableSpace =
              windowHeight - rect.top - STEP_FORM_BUTTONS_HEIGHT - 60;
            setDropdownMaxHeight(Math.max(150, availableSpace));
          }
        }
      };

      // Calculate on mount and when window resizes
      calculateHeight();
      window.addEventListener('resize', calculateHeight);
      return () => window.removeEventListener('resize', calculateHeight);
    }
  }, [formType, item?.control]);

  if (!item) return null;

  return (
    <View
      style={[
        style.questionItemWrapper,
        { width, position: 'relative', zIndex: 100000 },
      ]}
    >
      {formType !== 'step-form' ? (
        <>
          <QuestionMarker
            questionMarker={questionMarker || 'Q'}
            index={index ?? item.index}
            styleConfig={styleConfig as IQuestionnaire['style']}
          />
          <Text
            style={[
              style.question,
              {
                fontFamily: styleConfig.fontFamily as string,
              },
            ]}
          >
            {item.question || '誰と旅行に行きますか？'}
          </Text>
        </>
      ) : null}

      {item.coverImage?.enabled ? (
        <>
          {((item.coverImage && !('type' in item.coverImage)) ||
            item.coverImage?.type === 'image') &&
          item.coverImage?.url ? (
            <Image
              source={{ uri: item.coverImage.url }}
              style={[
                {
                  marginBottom: 40,
                  width: styleConfig.coverImage?.size?.width || width - 40, // less total padding horizontal
                  height: styleConfig.coverImage?.size?.height || 223,
                  borderRadius: styleConfig.coverImage?.borderRadius || 0,
                  borderWidth: styleConfig.coverImage?.borderWidth || 0,
                  borderColor:
                    styleConfig.coverImage?.borderColor || '@primaryColor',
                },
                { resizeMode: item.coverImage?.cropping },
              ]}
            />
          ) : null}
          {item.coverImage?.type === 'lottie' && item.coverImage?.lottieUrl ? (
            <>
              <Lottie
                source={{ uri: item.coverImage.lottieUrl }}
                onAnimationFailure={(error) => {
                  console.error('===Lottie Error===', error);
                  setLottieError(error);
                }}
                webStyle={{
                  marginBottom: 40,
                  ...(item.coverImage?.cropping === 'cover'
                    ? {
                        width: Math.max(width - 40, 223),
                        height: Math.max(width - 40, 223),
                      }
                    : {
                        width: width - 40, // less total padding horizontal
                        height: 223,
                      }),
                }}
                style={{
                  marginBottom: 40,
                  width: width - 40, // less total padding horizontal
                  height: 223,
                }}
                loop={item.coverImage?.loop}
                resizeMode={item.coverImage?.cropping}
                autoPlay
              />
              {lottieError ? (
                <Text style={{ color: '#FF0000' }}>{lottieError}</Text>
              ) : (
                <></>
              )}
            </>
          ) : null}
        </>
      ) : null}

      <ItemControl
        item={item}
        width={width}
        dropdownContainerRef={dropdownContainerRef}
        styleConfig={styleConfig}
        dropdownMaxHeight={dropdownMaxHeight}
      />

      {item.note?.enabled ? (
        <Text
          style={{
            textAlign: styleConfig.textAlign,
            fontSize: styleConfig.note?.fontSize || 12,
            fontFamily: styleConfig.fontFamily,
            color: styleConfig.note?.color || '#6C777C',
            marginTop: 16,
            width: '100%',
          }}
        >
          {item.note?.text}
        </Text>
      ) : null}
    </View>
  );
};

const createStyles = (style: any) => {
  let { width } = useWindowDimensions();

  return StyleSheet.create({
    questionItemWrapper: {
      display: 'flex',
      alignItems: 'center',
      width,
      paddingHorizontal: 20,
      paddingBottom: 48,
    },
    question: {
      fontWeight: '700',
      marginVertical: 16,
      color: style.question?.color || '#1A2737',
      fontSize: style.question?.fontSize || 24,
    },
  });
};

export default Question;
